/** @jsx jsx */
import React, {
	Fragment,
	memo,
	type PropsWithChildren,
	type RefObject,
	useCallback,
	useEffect,
	useState,
	useMemo,
} from 'react';
import { styled, css, jsx } from '@compiled/react';
import { triggerPostMoveFlash } from '@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';
import { token, type CSSToken } from '@atlaskit/tokens';
import {
	COLUMN_ID_CHECKBOX,
	COLUMN_ID_EXPAND_BUTTON,
	COLUMN_ID_MERGED_COLUMN,
} from '@atlassian/jira-issue-table-common/src/constants.tsx';
import { ScreenReaderCaption } from '@atlassian/jira-accessibility/src/common/ui/screenreader-text/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useVisibleDepth } from '@atlassian/jira-issue-table-hierarchy/src/controllers/hierarchy/index.tsx';
import type { Props as AsyncColumnPickerProps } from '@atlassian/jira-issue-table-column-picker/src/ui/main.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import { InsertColumnButton } from '../insert-column-button/index.tsx';
import {
	COLUMN_ID_MEATBALL_MENU,
	MOVE_COLUMN_FIRST,
	MOVE_COLUMN_LAST,
	MOVE_COLUMN_LEFT,
	MOVE_COLUMN_RIGHT,
	EXPAND_CELL_PADDING,
} from '../../../common/constants.tsx';
import { getExpandColumnWidth } from '../utils.tsx';
import type {
	HeaderHeight,
	Column,
	ColumnId,
	ReorderColumn,
	TargetColumnPosition,
	BorderWidth,
	ColumnResizeHandleType,
	CustomComponents,
} from '../../../common/types.tsx';
import { useCustomComponents } from '../../../controllers/custom-components/index.tsx';
import { useElementSizeSelector } from '../../../controllers/element-size/index.tsx';
import {
	useIsFixedTableLayoutEnabled,
	useIsInBetweenColumnPickerEnabled,
	useIsInlineFieldConfigEnabled,
	useIsTimelineEnabled,
	useIsChildIssuesTableVisualUpliftEnabled,
	useIsGridNavigationEnabled,
} from '../../../controllers/features/selectors.tsx';
import { useScrollStateSelector } from '../../../controllers/scroll-state/index.tsx';
import {
	InlineFieldCreateOnboardingProvider,
	InlineFieldCreateOnboarding,
} from '../../inline-field-create-onboarding/index.tsx';
import { HeaderCell } from './header-cell/index.tsx';
import messages from './messages.tsx';

const BORDER_WIDTH: BorderWidth = '1px';
const RESIZE_HANDLE_Z_INDEX = 1;

const MaybeInlineFieldCreateOnboardingProvider = componentWithCondition(
	() => fg('jira_inline_field_config_gate'),
	InlineFieldCreateOnboardingProvider,
	({ children }: PropsWithChildren<{}>) => children,
);

const baseHeaderContainerCellStyles = {
	backgroundColor: token('elevation.surface.sunken'),
	borderColor: token('color.border'),
	borderStyle: 'solid',
	borderTopWidth: 0,
	borderBottomWidth: BORDER_WIDTH,
	borderLeftWidth: 0,
	borderRightWidth: BORDER_WIDTH,
	'&:first-of-type': {
		borderTopLeftRadius: token('border.radius.200'),
	},
	'&:last-of-type': {
		borderTopRightRadius: token('border.radius.200'),
		borderRightWidth: 0,
	},
} as const;

const baseHeaderContainerCellKeyboardStyles = {
	backgroundColor: token('elevation.surface.sunken'),
	borderColor: token('color.border'),
	borderStyle: 'solid',
	borderTopWidth: 0,
	borderBottomWidth: BORDER_WIDTH,
	borderLeftWidth: 0,
	borderRightWidth: BORDER_WIDTH,
	'&:first-of-type': {
		borderTopLeftRadius: token('border.radius.200'),
		'&:focus::before': {
			left: '-1px',
			width: 'calc(100% + 3px)',
			height: 'calc(100% + 4px)',
			borderTopLeftRadius: token('border.radius.200'),
		},
	},
	'&:last-of-type': {
		borderTopRightRadius: token('border.radius.200'),
		borderRightWidth: 0,
		'&:focus::before': {
			borderTopRightRadius: token('border.radius.200'),
		},
	},
	'&:focus': {
		outline: 'unset',
		'&:before': {
			content: ' ',
			boxSizing: 'border-box',
			display: 'block',
			position: 'absolute',
			height: 'calc(100% + 4px)',
			width: 'calc(100% + 5px)',
			inset: '0px',
			left: '-3px',
			top: '-2px',
			border: `2px solid ${token('color.border.focused')}`,
		},
	},
} as const;

const HEADER_HEIGHT: HeaderHeight = 40;
const STICKY_CELL_Z_INDEX = RESIZE_HANDLE_Z_INDEX + 1;

type Props = {
	columns: Column[];
	canViewColumnConfiguration: boolean;
	canEditColumnConfiguration: boolean;
	isCompactColumnEnabled?: boolean;
	resizeHandleType?: ColumnResizeHandleType;
	onColumnsChange?: (columns: Column[]) => void;
	onResizeColumn?: (columnId: ColumnId, width: number | undefined) => void;
	tableRef?: RefObject<HTMLTableElement>;
	tableBorderRadius?: CSSToken;
	columnPickerProps?: AsyncColumnPickerProps;
	showNoColumns?: boolean;
	resizingColumnIndex?: number;
	setResizingColumnIndex?: (resizingColumnIndex: number | undefined) => void;
};

/**
 * Runs a flash animation on the background color of the column that has been reordered,
 * in order to highlight where the column has moved to.
 */
const triggerColumnFlash = (table: HTMLElement, index: number) => {
	const columnCellsSelector = `th:nth-of-type(${index + 1}), td:nth-of-type(${index + 1})`;

	Array.from(table.querySelectorAll(columnCellsSelector))
		.filter((element): element is HTMLElement => element instanceof HTMLElement)
		.forEach((element) => triggerPostMoveFlash(element));
};

type ReorderOperation = {
	finishIndex: number;
};

export const Header = memo<Props>(
	({
		columnPickerProps,
		columns,
		canViewColumnConfiguration,
		canEditColumnConfiguration,
		isCompactColumnEnabled = false,
		resizeHandleType,
		onColumnsChange,
		onResizeColumn,
		tableRef,
		tableBorderRadius,
		showNoColumns,
		resizingColumnIndex,
		setResizingColumnIndex,
	}: Props) => {
		const { formatMessage } = useIntl();
		const isTimelineEnabled = useIsTimelineEnabled();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		let customComponents: CustomComponents | null = null;

		if (fg('jira_native_issue_table_extra_custom_components')) {
			// eslint-disable-next-line react-hooks/rules-of-hooks
			customComponents = useCustomComponents();
		}

		// Unique identifier for the header of this particular issue table instance
		const [instanceId] = useState(() => Symbol('pdnd-issue-table-header'));

		const [lastReorderOperation, setLastReorderOperation] = useState<ReorderOperation | null>(null);

		const [screenReaderMessage, setScreenReaderMessage] = useState('');

		const reorderColumn: ReorderColumn = useCallback(
			({ startIndex, indexOfTarget, closestEdgeOfTarget = null, analyticsAttributes = {} }) => {
				const finishIndex = getReorderDestinationIndex({
					axis: 'horizontal',
					startIndex,
					indexOfTarget,
					closestEdgeOfTarget,
				});

				if (startIndex === finishIndex) {
					return;
				}

				const newColumns = reorder({
					list: columns,
					startIndex,
					finishIndex,
				});

				onColumnsChange?.(newColumns);

				setLastReorderOperation({ finishIndex });

				setScreenReaderMessage(
					formatMessage(messages.movedColumn, {
						column: columns[startIndex]?.title,
						previousPosition: startIndex + 1,
						position: finishIndex + 1,
						// TODO: EM-7165 update to columns.length when excluding column picker
						total: columns.length - 1,
					}),
				);

				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'reordered',
						actionSubject: 'columns',
					}),
					'issueTable',
					analyticsAttributes,
				);
			},
			[columns, createAnalyticsEvent, formatMessage, onColumnsChange],
		);

		const stickyColumnsWidth = fg('issue_table_frozen_columns')
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useMemo(() => {
					let totalWidth = 0;

					columns.forEach((column) => {
						const { id, isColumnSticky, defaultWidth, customWidth } = column;
						if (
							id === COLUMN_ID_CHECKBOX ||
							id === COLUMN_ID_EXPAND_BUTTON ||
							id === COLUMN_ID_MERGED_COLUMN ||
							isColumnSticky
						) {
							if (id === COLUMN_ID_EXPAND_BUTTON) {
								totalWidth += defaultWidth + EXPAND_CELL_PADDING;
							} else {
								totalWidth += customWidth ?? defaultWidth;
							}
						}
					});
					return totalWidth;
				}, [columns])
			: 0;

		// We use an object for lastReorderOperation so we can trigger the column flash
		// if the finish index is the same as the previous reorder operation.
		useEffect(() => {
			const table = tableRef?.current;

			if (table && lastReorderOperation !== null) {
				triggerColumnFlash(table, lastReorderOperation.finishIndex);
			}
		}, [lastReorderOperation, tableRef]);

		useEffect(() => {
			if (!canEditColumnConfiguration) {
				return;
			}

			return monitorForElements({
				canMonitor({ source }) {
					// Only monitor this particular instance id
					// (i.e. prevent monitoring entities that are not headers, or headers from another issue table)
					return source.data.instanceId === instanceId;
				},
				onDrop({ source, location }) {
					const [destination] = location.current.dropTargets;

					if (!destination) {
						return;
					}

					const startIndex = columns.findIndex((column) => column.id === source.data.id);

					if (startIndex === -1) {
						return;
					}

					const indexOfTarget = columns.findIndex((column) => column.id === destination.data.id);

					if (indexOfTarget === -1) {
						return;
					}

					const closestEdgeOfTarget = extractClosestEdge(destination.data);

					reorderColumn({
						startIndex,
						indexOfTarget,
						closestEdgeOfTarget,
						analyticsAttributes: { operation: 'drop' },
					});
				},
			});
		}, [columns, canEditColumnConfiguration, instanceId, reorderColumn, tableRef]);

		const onMoveColumn = (index: number, position: TargetColumnPosition) => {
			if (canEditColumnConfiguration && onColumnsChange !== undefined) {
				switch (position) {
					case MOVE_COLUMN_FIRST: {
						if (index > 0) {
							reorderColumn({
								startIndex: index,
								indexOfTarget: 0,
								closestEdgeOfTarget: 'left',
								analyticsAttributes: { operation: 'move-first' },
							});
						}
						break;
					}
					case MOVE_COLUMN_LEFT: {
						if (index > 0) {
							reorderColumn({
								startIndex: index,
								indexOfTarget: index - 1,
								closestEdgeOfTarget: 'left',
								analyticsAttributes: { operation: 'move-left' },
							});
						}
						break;
					}
					case MOVE_COLUMN_RIGHT: {
						// TODO: EM-7165 change this to `columns.length - 1` when excluding column picker
						if (index < columns.length - 2) {
							reorderColumn({
								startIndex: index,
								indexOfTarget: index + 1,
								closestEdgeOfTarget: 'right',
								analyticsAttributes: { operation: 'move-right' },
							});
						}
						break;
					}
					case MOVE_COLUMN_LAST: {
						// TODO: EM-7165 change this to `columns.length - 1` when excluding column picker
						if (index < columns.length - 2) {
							reorderColumn({
								startIndex: index,
								// TODO: EM-7165 change this to `columns.length - 1` when excluding column picker
								indexOfTarget: columns.length - 2,
								closestEdgeOfTarget: 'right',
								analyticsAttributes: { operation: 'move-last' },
							});
						}
						break;
					}
					default:
						throw new Error(`Unexpected target column position ${position}`);
				}
			}
		};

		const onRemoveColumn = (index: number) => {
			if (canEditColumnConfiguration && onColumnsChange !== undefined) {
				const newColumns = [...columns];
				newColumns.splice(index, 1);
				onColumnsChange(newColumns);
			}
		};

		const tableWidth = useElementSizeSelector((size) => size.width);

		const { hasShadowBottom, hasShadowLeft } = useScrollStateSelector((scrollState) => {
			const { scrollTop, scrollLeft, width: containerWidth } = scrollState;

			return {
				hasShadowBottom: scrollTop > 0,
				// Scroll measurements are fractional, while width measurements are integer, so we need to round before comparing
				hasShadowLeft:
					containerWidth < tableWidth && Math.round(scrollLeft) < tableWidth - containerWidth,
			};
		});

		const isFixedTableLayoutEnabled = useIsFixedTableLayoutEnabled();
		const isInBetweenColumnPickerEnabled = useIsInBetweenColumnPickerEnabled();
		const isInlineFieldConfigEnabled = useIsInlineFieldConfigEnabled();
		const isGridNavigationEnabled = useIsGridNavigationEnabled();

		let visibleDepth = 0;
		if (fg('jsc_m2_hierarchy_fe_changes')) {
			// eslint-disable-next-line react-hooks/rules-of-hooks
			visibleDepth = useVisibleDepth();
		}

		let isChildIssuesTableVisualUpliftEnabled = false;
		if (fg('fg_visual_uplift_for_child_issues_table')) {
			// eslint-disable-next-line react-hooks/rules-of-hooks
			isChildIssuesTableVisualUpliftEnabled = useIsChildIssuesTableVisualUpliftEnabled();
		}

		const [isDropdownOpen, setIsDropdownOpen] = useState(false);

		const handleDropdownStateChange = (isOpen: boolean) => {
			setIsDropdownOpen(isOpen);
		};

		const content = (
			<tr>
				{columns.map((column, index) => {
					const { isColumnSticky, stickyOffsets } = column;
					const isExpandColumn =
						column.id === COLUMN_ID_EXPAND_BUTTON && fg('jsc_m2_hierarchy_fe_changes');

					const isMergedColumn =
						column.id === COLUMN_ID_MERGED_COLUMN && fg('jira_list_merged_column_gate');

					const isMeatballMenu = column.id === COLUMN_ID_MEATBALL_MENU;

					const defaultColumnWidth = column.customWidth ?? column.defaultWidth;
					const columnWidth =
						isExpandColumn || isMergedColumn
							? getExpandColumnWidth(visibleDepth)
							: defaultColumnWidth;

					let hasShadow = isExpandColumn ? false : canViewColumnConfiguration && hasShadowLeft;
					if (fg('jira_list_merged_column_gate')) {
						hasShadow = isMeatballMenu && canViewColumnConfiguration && hasShadowLeft;
					}

					let customHeader = column.customHeader;

					if (
						isTimelineEnabled &&
						customComponents?.timeline?.ColumnHeader &&
						column.id === 'duedate' // Temporary value until https://jbusiness.atlassian.net/browse/JSC-2099 is implemented
					) {
						customHeader = <customComponents.timeline.ColumnHeader />;
					}

					return (
						// TODO: EM-7165 – refactor column picker logic, separating it from regular columns
						<Fragment key={column.id}>
							{
								// Empty cell to fill remaining space if configured columns are not wide enough to fill the table
								isFixedTableLayoutEnabled && column.id === COLUMN_ID_MEATBALL_MENU && (
									<EmptyHeaderCell aria-hidden />
								)
							}
							{customHeader ? (
								<HeaderCellContainer
									scope="col"
									columnWidth={columnWidth}
									// aria-label is added to prevent the extra accessibility hints in the header cell from being
									// read as the column title when a user is navigating between columns in the table content.
									aria-label={column.title}
									isSticky={canViewColumnConfiguration}
									hasShadow={hasShadow}
									visualRefresh={isVisualRefreshEnabled()}
									isCompactColumnEnabled={isCompactColumnEnabled}
									tableBorderRadius={tableBorderRadius}
									index={index}
									{...(fg('nin-grid-keyboard-navigation') && { tabIndex: index === 0 ? 0 : -1 })}
									{...(fg('issue_table_frozen_columns') && {
										isColumnSticky,
										stickyOffsets,
									})}
									isChildIssuesTableVisualUpliftEnabled={isChildIssuesTableVisualUpliftEnabled}
									isGridNavigationEnabled={isGridNavigationEnabled}
								>
									{customHeader}
								</HeaderCellContainer>
							) : (
								<HeaderCell
									column={column}
									hasColumnConfiguration={canEditColumnConfiguration}
									instanceId={instanceId}
									isCompactColumnEnabled={isCompactColumnEnabled}
									isFirst={index === 0}
									// TODO: EM-7165 change this to `columns.length - 1` when excluding column picker
									isLast={index === columns.length - 2}
									previousColumn={index === 0 ? undefined : columns[index - 1]}
									resizeHandleType={resizeHandleType}
									tableRef={tableRef}
									tableBorderRadius={tableBorderRadius}
									onMoveColumn={(position) => onMoveColumn(index, position)}
									onDropdownStateChange={handleDropdownStateChange}
									onRemoveColumn={() => onRemoveColumn(index)}
									onResizeColumn={onResizeColumn}
									index={index}
									{...(fg('issue_table_frozen_columns') && {
										stickyColumnsWidth,
										resizingColumnIndex,
										setResizingColumnIndex,
									})}
									{...(fg('nin-grid-keyboard-navigation') && { tabIndex: index === 0 ? 0 : -1 })}
								/>
							)}
						</Fragment>
					);
				})}
			</tr>
		);

		return (
			<>
				<MaybeInlineFieldCreateOnboardingProvider>
					{isInBetweenColumnPickerEnabled && (
						<InsertColumnButton columns={columns} columnPickerProps={columnPickerProps} />
					)}
					{(isInlineFieldConfigEnabled || isInBetweenColumnPickerEnabled) &&
						!showNoColumns &&
						fg('jira_inline_field_config_gate') && <InlineFieldCreateOnboarding />}
				</MaybeInlineFieldCreateOnboardingProvider>
				<ScreenReaderCaption
					aria-live="assertive"
					data-testid="native-issue-table.ui.issue-table.header.screen-reader-caption"
				>
					{screenReaderMessage}
				</ScreenReaderCaption>
				<thead
					css={[
						fg('issue_table_frozen_columns') ? baseStickyHeaderStyles : baseStickyHeaderStylesOld,
						isDropdownOpen && increasedZIndexStyles,
						hasShadowBottom && fg('reduce_size_of_scroll_container_shadow') && shadowStyles,
						hasShadowBottom && !fg('reduce_size_of_scroll_container_shadow') && shadowStylesOld,
					]}
				>
					{content}
				</thead>
			</>
		);
	},
);

const baseStickyHeaderStylesOld = css({
	position: 'sticky',
	top: 0,
	zIndex: 2,
});

// When a column is frozen, the table cell has z-index: 2. The header must have a higher z-index(3) to avoid being visually covered by the cell during vertical scrolling.
const baseStickyHeaderStyles = css({
	position: 'sticky',
	top: 0,
	zIndex: 3,
});

const increasedZIndexStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	zIndex: layers.card + 1,
});

// remove when cleaning up reduce_size_of_scroll_container_shadow
const shadowStylesOld = css({
	boxShadow: `
	  0px 8px 8px -8px ${token('elevation.shadow.overflow.spread')},
	  0px 1px 1px -1px ${token('elevation.shadow.overflow.perimeter')}
	`,
});

const shadowStyles = css({
	boxShadow: token('elevation.shadow.overflow'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const HeaderCellContainerStyledCell = styled.th<{
	columnWidth?: number;
	minColumnWidth?: number;
	isDraggable?: boolean;
	isHovered?: boolean;
	isSticky?: boolean;
	hasShadow?: boolean;
	visualRefresh: boolean;
	isCompactColumnEnabled: boolean;
	index: number;
	tableBorderRadius?: CSSToken;
	isColumnSticky?: boolean;
	stickyOffsets?: number;
	isChildIssuesTableVisualUpliftEnabled: boolean;
}>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Fix for compiled prop bug with inheritance
	baseHeaderContainerCellStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ tableBorderRadius }) => ({
		position: 'relative',
		boxSizing: 'border-box',
		height: `${HEADER_HEIGHT}px`,
		maxHeight: `${HEADER_HEIGHT}px`,
		minHeight: `${HEADER_HEIGHT}px`,
		paddingBlock: token('space.0'),
		paddingInline: token('space.100'),
		verticalAlign: 'middle',
		whiteSpace: 'nowrap',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'&:first-of-type': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
			borderTopLeftRadius: `${tableBorderRadius ?? token('space.100')}`,
			// Overrides @atlaskit/css-reset styles
			paddingLeft: token('space.100'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'&:last-of-type': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
			borderTopRightRadius: `${tableBorderRadius ?? token('space.100')}`,
			// Overrides @atlaskit/css-reset styles
			paddingRight: token('space.100'),
		},
	}),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ columnWidth, minColumnWidth, isCompactColumnEnabled }) => ({
		// Width allows overrides when table is in fixed layout mode
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		'--local-column-width': columnWidth != null ? `${columnWidth}px` : 'auto',

		width: 'var(--local-resizing-width, var(--local-column-width))',
		// Min-width allows overrides when table is in auto layout mode
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		'--local-column-min-width': minColumnWidth != null ? `${minColumnWidth}px` : 'auto',
		minWidth: 'var(--local-resizing-width, var(--local-column-min-width))',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:first-of-type': {
			paddingLeft:
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
				isCompactColumnEnabled && columnWidth && columnWidth < 40 ? '0px' : token('space.100'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:last-of-type': {
			paddingRight:
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
				isCompactColumnEnabled && columnWidth && columnWidth < 40 ? '0px' : token('space.100'),
		},
	}),
	// Programmatic `isHovered` state is used here so hover state isn't displayed when hovering the resize handle
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isDraggable, isHovered }) =>
		isDraggable &&
		isHovered && {
			':hover': {
				cursor: 'grab',
				backgroundColor: token('elevation.surface.hovered'),
			},
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isSticky, isColumnSticky, stickyOffsets }) =>
		(isSticky || isColumnSticky) && {
			position: 'sticky',
			zIndex: STICKY_CELL_Z_INDEX,
			...(isSticky && { right: 0 }),
			...(isColumnSticky && { left: `${stickyOffsets}px` }),
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ hasShadow }) =>
		hasShadow && {
			boxShadow: `
                -8px 0 8px -8px ${token('elevation.shadow.overflow.spread')},
                -1px 0 1px -1px ${token('elevation.shadow.overflow.perimeter')}
            `,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			borderLeftWidth: `${BORDER_WIDTH} !important`,
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ visualRefresh }) =>
		visualRefresh
			? { color: token('color.text.subtle'), font: token('font.heading.xxsmall') }
			: {
					color: token('color.text.accent.gray'),
					fontWeight: token('font.weight.medium'),
				},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ isChildIssuesTableVisualUpliftEnabled }) =>
		isChildIssuesTableVisualUpliftEnabled && {
			backgroundColor: 'none',
			borderRightWidth: '0',
		},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const HeaderCellContainerNew = styled.th<{
	columnWidth?: number;
	minColumnWidth?: number;
	isDraggable?: boolean;
	isHovered?: boolean;
	isSticky?: boolean;
	hasShadow?: boolean;
	visualRefresh: boolean;
	isCompactColumnEnabled: boolean;
	tableBorderRadius?: CSSToken;
	isColumnSticky?: boolean;
	stickyOffsets?: number;
	isChildIssuesTableVisualUpliftEnabled: boolean;
	isGridNavigationEnabled: boolean;
}>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ isGridNavigationEnabled }) =>
		isGridNavigationEnabled
			? { ...baseHeaderContainerCellKeyboardStyles }
			: { ...baseHeaderContainerCellStyles },
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ tableBorderRadius, isGridNavigationEnabled }) => ({
		position: 'relative',
		boxSizing: 'border-box',
		height: `${HEADER_HEIGHT}px`,
		maxHeight: `${HEADER_HEIGHT}px`,
		minHeight: `${HEADER_HEIGHT}px`,
		paddingBlock: token('space.0'),
		paddingInline: token('space.100'),
		verticalAlign: 'middle',
		whiteSpace: 'nowrap',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'&:first-of-type': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
			borderTopLeftRadius: `${tableBorderRadius ?? token('space.100')}`,
			// Overrides @atlaskit/css-reset styles
			paddingLeft: token('space.100'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'&:last-of-type': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
			borderTopRightRadius: `${tableBorderRadius ?? token('space.100')}`,
			// Overrides @atlaskit/css-reset styles
			paddingRight: token('space.100'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		...(isGridNavigationEnabled && {
			'&:focus': {
				outline: 'unset',
				borderColor: 'color.border.focused',
				borderStyle: 'solid',
				borderWidth: '2px',
			},
		}),
	}),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ columnWidth, minColumnWidth, isCompactColumnEnabled }) => ({
		// Width allows overrides when table is in fixed layout mode
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		'--local-column-width': columnWidth != null ? `${columnWidth}px` : 'auto',

		width: 'var(--local-resizing-width, var(--local-column-width))',
		// Min-width allows overrides when table is in auto layout mode
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		'--local-column-min-width': minColumnWidth != null ? `${minColumnWidth}px` : 'auto',
		minWidth: 'var(--local-resizing-width, var(--local-column-min-width))',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:first-of-type': {
			paddingLeft:
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
				isCompactColumnEnabled && columnWidth && columnWidth < 40 ? '0px' : token('space.100'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:last-of-type': {
			paddingRight:
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
				isCompactColumnEnabled && columnWidth && columnWidth < 40 ? '0px' : token('space.100'),
		},
	}),
	// Programmatic `isHovered` state is used here so hover state isn't displayed when hovering the resize handle
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isDraggable, isHovered }) =>
		isDraggable &&
		isHovered && {
			':hover': {
				cursor: 'grab',
				backgroundColor: token('elevation.surface.hovered'),
			},
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isSticky, isColumnSticky, stickyOffsets }) =>
		(isSticky || isColumnSticky) && {
			position: 'sticky',
			zIndex: STICKY_CELL_Z_INDEX,
			...(isSticky && { right: 0 }),
			...(isColumnSticky && { left: `${stickyOffsets}px` }),
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ hasShadow }) =>
		hasShadow && {
			boxShadow: `
                -8px 0 8px -8px ${token('elevation.shadow.overflow.spread')},
                -1px 0 1px -1px ${token('elevation.shadow.overflow.perimeter')}
            `,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			borderLeftWidth: `${BORDER_WIDTH} !important`,
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ visualRefresh }) =>
		visualRefresh
			? { color: token('color.text.subtle'), font: token('font.heading.xxsmall') }
			: {
					color: token('color.text.accent.gray'),
					fontWeight: token('font.weight.medium'),
				},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ isChildIssuesTableVisualUpliftEnabled }) =>
		isChildIssuesTableVisualUpliftEnabled && {
			backgroundColor: 'none',
			borderRightWidth: '0',
		},
);

// Exported for testing
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles
export const HeaderCellContainer = componentWithFG(
	'nin-grid-keyboard-navigation',
	HeaderCellContainerNew,
	HeaderCellContainerStyledCell,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const EmptyHeaderCell = styled.th(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Fix for compiled prop bug with inheritance
	baseHeaderContainerCellStyles,
	{
		borderLeftWidth: 0,
		borderRightWidth: 0,
	},
);
